import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import MobileFriendlyDatePicker from '../components/MobileFriendlyDatePicker';
import { Trash2, Plus, Users, FileText, Eye, AlertTriangle, Edit } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PupilsPage = () => {
  const [pupils, setPupils] = useState([]);
  const [pupilStats, setPupilStats] = useState({});
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    license_number: '', 
    test_booked: false, 
    test_date: null, 
    test_time: '',
    additional_needs: '',
    notes: '',
    additional_hours: 0,
    is_active: true 
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPupil, setEditingPupil] = useState(null);

  useEffect(() => {
    fetchPupils();
    fetchPupilStats();
  }, []); // Fetch functions are stable (defined in component scope)

  const fetchPupils = async () => {
    try {
      const response = await axios.get(`${API}/pupils`);
      setPupils(response.data);
    } catch (error) {
      console.error('Error fetching pupils:', error);
      toast.error('Failed to load pupils');
    }
  };

  const fetchPupilStats = async () => {
    try {
      const response = await axios.get(`${API}/reflection-logs`);
      const logs = response.data;
      const stats = {};
      
      logs.forEach(log => {
        const pupilId = log.pupil_id;
        if (!stats[pupilId]) {
          stats[pupilId] = { logCount: 0, totalHours: 0 };
        }
        stats[pupilId].logCount += 1;
        stats[pupilId].totalHours += log.lesson_hours || 1.0;
      });
      
      // Round hours to 1 decimal place
      Object.keys(stats).forEach(pupilId => {
        stats[pupilId].totalHours = Math.round(stats[pupilId].totalHours * 10) / 10;
      });
      
      setPupilStats(stats);
    } catch (error) {
      console.error('Error fetching pupil stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    if (formData.test_booked && !formData.test_date) {
      toast.error('Test date is required when test is booked');
      return;
    }
    
    if (formData.test_booked && !formData.test_time) {
      toast.error('Test time is required when test is booked');
      return;
    }

    try {
      // Format the data to send
      const dataToSend = {
        ...formData,
        test_date: formData.test_date ? formData.test_date.toISOString() : null,
        test_time: formData.test_time || null
      };
      
      await axios.post(`${API}/pupils`, dataToSend);
      toast.success('Pupil added successfully');
      setFormData({ name: '', email: '', phone: '', license_number: '', test_booked: false, test_date: null, test_time: '', additional_needs: '', notes: '', additional_hours: 0, is_active: true });
      setIsDialogOpen(false);
      fetchPupils();
      fetchPupilStats();
    } catch (error) {
      console.error('Error adding pupil:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to add pupil');
      }
    }
  };

  const handleEdit = (pupil) => {
    setEditingPupil(pupil);
    setFormData({
      name: pupil.name || '',
      email: pupil.email || '',
      phone: pupil.phone || '',
      license_number: pupil.license_number || '',
      test_booked: pupil.test_booked || false,
      test_date: pupil.test_date ? new Date(pupil.test_date) : null,
      test_time: pupil.test_time || '',
      additional_needs: pupil.additional_needs || '',
      notes: pupil.notes || '',
      additional_hours: pupil.additional_hours || 0,
      is_active: pupil.is_active !== undefined ? pupil.is_active : true
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    if (formData.test_booked && !formData.test_date) {
      toast.error('Test date is required when test is booked');
      return;
    }
    
    if (formData.test_booked && !formData.test_time) {
      toast.error('Test time is required when test is booked');
      return;
    }

    try {
      // Format the data to send
      const dataToSend = {
        ...formData,
        test_date: formData.test_date ? formData.test_date.toISOString() : null,
        test_time: formData.test_time || null
      };
      
      await axios.put(`${API}/pupils/${editingPupil.id}`, dataToSend);
      toast.success('Pupil updated successfully');
      setFormData({ name: '', email: '', phone: '', license_number: '', test_booked: false, test_date: null, test_time: '', additional_needs: '', notes: '', additional_hours: 0, is_active: true });
      setIsEditDialogOpen(false);
      setEditingPupil(null);
      fetchPupils();
      fetchPupilStats();
    } catch (error) {
      console.error('Error updating pupil:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to update pupil');
      }
    }
  };

  const handleDelete = async (pupilId) => {
    if (!window.confirm('Are you sure you want to delete this pupil? This will also remove their reflection logs.')) return;
    
    try {
      await axios.delete(`${API}/pupils/${pupilId}`);
      toast.success('Pupil deleted successfully');
      fetchPupils();
      fetchPupilStats();
    } catch (error) {
      console.error('Error deleting pupil:', error);
      toast.error('Failed to delete pupil');
    }
  };

  const handleClearPupilLogs = async (pupilId, pupilName) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete ALL reflection logs for ${pupilName} and clear all their hours. This action cannot be undone. Are you sure?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/pupils/${pupilId}/logs`);
      toast.success(`All logs cleared for ${pupilName} (${response.data.deleted_count} logs deleted)`);
      fetchPupilStats(); // Refresh stats
    } catch (error) {
      console.error('Error clearing pupil logs:', error);
      toast.error('Failed to clear pupil logs');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">Pupils</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">Manage your driving pupils and their information</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2" size={16} />
              Add New Pupil
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Pupil</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter pupil's name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Enter email address (for reflection log notifications)"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <Label htmlFor="license_number">License Number</Label>
                <Input
                  id="license_number"
                  value={formData.license_number}
                  onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                  placeholder="Enter driving license number (optional)"
                />
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="test_booked"
                    checked={formData.test_booked}
                    onCheckedChange={(checked) => setFormData({...formData, test_booked: checked})}
                  />
                  <Label htmlFor="test_booked">Test Booked</Label>
                </div>
                
                {formData.test_booked && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="test_date">Test Date *</Label>
                      <MobileFriendlyDatePicker
                        selected={formData.test_date}
                        onSelect={(date) => setFormData({...formData, test_date: date})}
                        placeholder="Pick a date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="test_time">Test Time *</Label>
                      <Input
                        id="test_time"
                        type="time"
                        value={formData.test_time}
                        onChange={(e) => setFormData({...formData, test_time: e.target.value})}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="additional_needs">Additional Needs</Label>
                  <Textarea
                    id="additional_needs"
                    value={formData.additional_needs}
                    onChange={(e) => setFormData({...formData, additional_needs: e.target.value})}
                    placeholder="Enter any additional needs, autism notes, or other important information..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Enter any additional notes..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="additional_hours">Additional Hours (external lessons)</Label>
                  <Input
                    id="additional_hours"
                    type="number"
                    min="0"
                    step="0.5"
                    value={formData.additional_hours}
                    onChange={(e) => setFormData({...formData, additional_hours: parseFloat(e.target.value) || 0})}
                    placeholder="e.g. hours from Total Drive, private practice"
                    data-testid="additional-hours-input"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
                <Label htmlFor="is_active" className="text-sm">
                  Active Pupil (uncheck to mark as inactive/graduated)
                </Label>
              </div>
              
              <Button type="submit" className="w-full">Add Pupil</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Pupil Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Pupil</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label htmlFor="edit_name">Name *</Label>
                <Input
                  id="edit_name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter pupil's name"
                />
              </div>
              <div>
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Enter email address (for reflection log notifications)"
                />
              </div>
              <div>
                <Label htmlFor="edit_phone">Phone</Label>
                <Input
                  id="edit_phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <Label htmlFor="edit_license_number">License Number</Label>
                <Input
                  id="edit_license_number"
                  value={formData.license_number}
                  onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                  placeholder="Enter driving license number (optional)"
                />
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit_test_booked"
                    checked={formData.test_booked}
                    onCheckedChange={(checked) => setFormData({...formData, test_booked: checked})}
                  />
                  <Label htmlFor="edit_test_booked">Test Booked</Label>
                </div>
                
                {formData.test_booked && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit_test_date">Test Date *</Label>
                      <MobileFriendlyDatePicker
                        selected={formData.test_date}
                        onSelect={(date) => setFormData({...formData, test_date: date})}
                        placeholder="Pick a date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_test_time">Test Time *</Label>
                      <Input
                        id="edit_test_time"
                        type="time"
                        value={formData.test_time}
                        onChange={(e) => setFormData({...formData, test_time: e.target.value})}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="edit_additional_needs">Additional Needs</Label>
                  <Textarea
                    id="edit_additional_needs"
                    value={formData.additional_needs}
                    onChange={(e) => setFormData({...formData, additional_needs: e.target.value})}
                    placeholder="Enter any additional needs, autism notes, or other important information..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit_notes">Notes</Label>
                  <Textarea
                    id="edit_notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Enter any additional notes..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit_additional_hours">Additional Hours (external lessons)</Label>
                  <Input
                    id="edit_additional_hours"
                    type="number"
                    min="0"
                    step="0.5"
                    value={formData.additional_hours}
                    onChange={(e) => setFormData({...formData, additional_hours: parseFloat(e.target.value) || 0})}
                    placeholder="e.g. hours from Total Drive, private practice"
                    data-testid="edit-additional-hours-input"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit_is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
                <Label htmlFor="edit_is_active" className="text-sm">
                  Active Pupil (uncheck to mark as inactive/graduated)
                </Label>
              </div>
              
              <Button type="submit" className="w-full">Update Pupil</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Clean List View */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {pupils.map((pupil) => (
              <div key={pupil.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                {/* Main Row - Always Visible */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => setEditingPupil(editingPupil?.id === pupil.id ? null : pupil)}
                >
                  <div className="flex items-center space-x-4">
                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${pupil.is_active ? 'bg-blue-500' : 'bg-gray-400'}`}>
                      {pupil.name.charAt(0).toUpperCase()}
                    </div>
                    
                    {/* Name and Status */}
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{pupil.name}</h3>
                        {!pupil.is_active && (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-gray-500 dark:text-green-600">
                        <span>{Math.round(((pupilStats[pupil.id]?.totalHours || 0) + (pupil.additional_hours || 0)) * 10) / 10}h</span>
                        <span>•</span>
                        <span>{pupilStats[pupil.id]?.logCount || 0} lessons</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Test Date Badge */}
                  <div className="flex items-center space-x-3">
                    {pupil.test_booked && pupil.test_date && (
                      <div className="flex flex-col items-end gap-1">
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                          Test: {new Date(pupil.test_date).toLocaleDateString()}
                          {pupil.test_time && ` @ ${pupil.test_time.substring(0, 5)}`}
                        </Badge>
                        {pupil.last_cancel_date && (
                          <Badge className="bg-red-100 text-red-700 border-red-300 font-semibold">
                            Last Cancel Date: {new Date(pupil.last_cancel_date).toLocaleDateString()}
                          </Badge>
                        )}
                      </div>
                    )}
                    <Eye size={18} className={`text-green-600 transition-transform ${editingPupil?.id === pupil.id ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                
                {/* Expanded Details - Shows when selected */}
                {editingPupil?.id === pupil.id && (
                  <div className="px-4 pb-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                      {/* Contact Info */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">Contact Details</h4>
                        {pupil.email ? (
                          <p className="text-sm text-gray-600 dark:text-green-600">
                            📧 {pupil.email}
                          </p>
                        ) : (
                          <p className="text-sm text-green-600 italic">No email</p>
                        )}
                        {pupil.phone ? (
                          <p className="text-sm text-gray-600 dark:text-green-600">
                            📱 {pupil.phone}
                          </p>
                        ) : (
                          <p className="text-sm text-green-600 italic">No phone</p>
                        )}
                        {pupil.license_number && (
                          <p className="text-sm text-gray-600 dark:text-green-600">
                            🪪 License: {pupil.license_number}
                          </p>
                        )}
                      </div>
                      
                      {/* Test Info */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">Test Information</h4>
                        {pupil.test_booked && pupil.test_date ? (
                          <>
                            <p className="text-sm text-blue-600 dark:text-blue-400">
                              📅 Test Date: {new Date(pupil.test_date).toLocaleDateString()}
                            </p>
                            {pupil.test_time && (
                              <p className="text-sm text-blue-600 dark:text-blue-400">
                                🕐 Test Time: {pupil.test_time?.substring(0, 5)}
                              </p>
                            )}
                            {pupil.last_cancel_date && (
                              <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded mt-1">
                                <p className="text-sm text-red-700 dark:text-red-400 font-bold">
                                  Last Cancel Date: {new Date(pupil.last_cancel_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-green-600 italic">No test booked</p>
                        )}
                      </div>
                      
                      {/* Additional Info */}
                      {(pupil.additional_needs || pupil.notes) && (
                        <div className="md:col-span-2 space-y-2">
                          {pupil.additional_needs && (
                            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded">
                              <p className="text-sm text-orange-700 dark:text-orange-300">
                                <strong>Additional Needs:</strong> {pupil.additional_needs}
                              </p>
                            </div>
                          )}
                          {pupil.notes && (
                            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded">
                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>Notes:</strong> {pupil.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <Link to={`/pupil/${pupil.id}/logs`}>
                        <Button variant="default" size="sm">
                          <FileText size={14} className="mr-1" />
                          View Logs
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(pupil);
                          setIsEditDialogOpen(true);
                        }}
                      >
                        <Edit size={14} className="mr-1" />
                        Edit
                      </Button>
                      {(pupilStats[pupil.id]?.logCount || 0) > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearPupilLogs(pupil.id, pupil.name);
                          }}
                        >
                          <AlertTriangle size={14} className="mr-1" />
                          Clear Logs
                        </Button>
                      )}
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(pupil.id);
                        }}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                    
                    <p className="text-xs text-green-600 mt-3">
                      Added: {new Date(pupil.created_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {pupils.length === 0 && (
        <Card className="text-center p-8">
          <CardContent>
            <Users size={48} className="mx-auto text-green-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No pupils yet</h3>
            <p className="text-gray-500">Add your first pupil to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


export default PupilsPage;
